import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Clock, Zap, Send, UploadIcon, CheckCircle } from 'lucide-react';
import { get, isNil } from 'lodash';

import Header from 'components/Header';
import { Grid, Column } from 'components/Layout';
import { Button } from 'components/Button';
import { Button as UIButton } from 'components/BankStatements/ui/button'
import { nextAction } from 'actions/workflow';
import NeedHelpModal from 'components/BankStatements/NeedHelpModal';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from 'components/BankStatements/ui/card';

import style from './style.scss';

const ConnectBankAccount = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');
  const applicationId = useSelector(state => get(state, 'workflow.entityId', get(state, 'workflow.attributes.entityId', '')));
  const data = useSelector(state => get(state, 'workflow.data.data') || get(state, 'workflow.state.data'));
  const isForkPrime = get(data, 'hasCompletedVerificationOnce');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!key) {
      navigate('/dashboard');
    } else if (isNil(data)) {
      navigate(`/applications/fsl/checkin?key=${key}`);
    }
  }, []);

  const handleButtonClick = (isDocumentUploadChosen = false) => {
    setIsSubmitting(true);
    dispatch(nextAction({
      url: `/workflows/fsl/${key}/next`,
      data: {
        isDocumentUploadChosen,
      },
      success: (res) => {
        setIsSubmitting(false);
        const nextUrl = get(res, 'data.url') || get(res, 'state.url');
        if (nextUrl) navigate(nextUrl);
      },
      fail: (error) => {
        setIsSubmitting(false);
        console.log(error);
      },
    }));
  }

  return (
    <>
      <Header />
      <section className="container section">
        <div className={cn('use-tailwind grid-container fluid', style['connect-bank-page'])}>
          <div className="grid-container">
            <Grid className="grid-margin-x max-limited">
              {applicationId ? (
                <div className="cell small-12 medium-12" style={{ textAlign: 'left' }}>
                  <p className="mb-0 p-large"><b>Application ID:</b> {applicationId}</p>
                </div>
              ) : null}

              <Column className="small-12 text-center">
                <div className="text-center my-8">
                  <h1 className="text-2xl md:text-3xl font-bold mb-2">Verify Your Bank Account</h1>
                  <p className="text-gray-600 mb-2">Choose your preferred method to verify and provide your banking information</p>
                </div>
              </Column>

              <Column className="small-12">
                <Card className="hover:shadow-lg transition-shadow border border-gray-100">
                  <CardHeader className="pb-4">
                    <div>
                      <CardTitle className="text-xl justify-between md:text-2xl md:justify-normal flex items-center gap-2 mb-2">
                        Instant Verification <span className="text-inherit hidden md:!inline">(Faster Processing)</span>
                        <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent text-secondary-foreground hover:bg-secondary/80 bg-gray-100">
                          Recommended
                        </div>
                      </CardTitle>
                      <div className="md:hidden text-gray-600 text-sm mb-2">(Faster Processing)</div>
                      <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-100">
                        {isForkPrime ? (
                          <p className="text-sm text-green-800 flex items-center">
                            <CheckCircle className="h-4 w-4 mr-2 text-green-600 flex-shrink-0" />
                            <div>
                              <span className="font-semibold">Special Offer: </span> 
                              You've already successfully verified a banking account and remain eligible for the $25 Credit! *
                            </div>
                          </p>
                        ) : (
                          <p className="text-xs md:text-sm text-green-800">
                            <span className="font-semibold">Special Offer:</span> Verify your account now and you could be eligible for a $25 Credit if you also enroll in Auto-Pay! *
                          </p>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex mb-6">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-2">
                          <Shield className="h-5 w-5 text-green-500" />
                          <span className="text-xs md:text-sm">Data safeguarded through encryption</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Zap className="h-5 w-5 text-green-500" />
                          <span className="text-xs md:text-sm">Instant verification</span>
                        </div>
                      </div>
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-2">
                          <Clock className="h-5 w-5 text-green-500" />
                          <span className="text-xs md:text-sm">Takes 2 minutes</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Send className="h-5 w-5 text-green-500" />
                          <span className="text-xs md:text-sm">Faster Funding</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      className="button green arrow text-white w-full h-12 text-base md:text-lg !border-0 font-normal p-0 mb-0"
                      onClick={() => handleButtonClick(false)}
                      isDisabled={isSubmitting}
                    >
                      {isForkPrime ? 'Verify another Bank Account' : 'Verify Now'}
                    </Button>

                    <p className="text-gray-500 text-xs md:text-sm text-center mt-3">
                      By clicking '{isForkPrime ? 'Verify another Bank Account' : 'Verify Now'}', you will securely connect your bank account through a trusted third-party service.
                    </p>
                  </CardContent>

                  <div className="flex items-center justify-center my-6">
                    <div className="flex-grow h-px bg-gray-200 mx-4"></div>
                    <div className="text-gray-400 font-medium text-sm">OR</div>
                    <div className="flex-grow h-px bg-gray-200 mx-4"></div>
                  </div>

                  <CardFooter className="flex flex-col items-center pt-0 pb-6">
                    <UIButton
                      onClick={() => handleButtonClick(true)}
                      variant="outline"
                      disabled={isSubmitting}
                      className="border-green-500 border-solid text-green-500 hover:bg-green-50 hover:text-green-600 hover:border-green-600 px-6 py-6 text-base md:text-lg"
                    >
                      <UploadIcon className="h-5 w-5" />
                      <span className="ml-2">Upload Bank Statements</span>
                    </UIButton>
                    <p className="text-xs text-green-500 mt-3 text-center md:text-left">
                      {isForkPrime
                        ? 'You can upload additional bank statements as needed. Your special offer eligibility is still valid.'
                        : 'Slower processing times and you won\'t be eligible for the $25 Credit.'
                      }
                    </p>
                  </CardFooter>
                </Card>
              </Column>

              <Column className="small-12 text-center mt-8 mb-4">
                <NeedHelpModal />
              </Column>

              <Column className="small-12 medium-12">
                <p className="text-xs/5 text-gray-500 mt-2">
                  * To qualify for the $25 Principal Reduction (“Principal Reduction”) Promotion (“Promotion”), you must: (i) verify and provide your banking information, for at least one banking account, by signing into your banking account through a specified third party service (“Instant Verification”), you will not qualify for the Promotion if you: (a) do not, or are unable to, Instantly Verify any of your banking information, or (b) upload, e-mail or otherwise electronically provide banking account statements without Instantly Verifying at least one banking account, or (c) mail or provide any paper banking account statements without Instantly Verifying at least one banking account; (ii) enroll in optional Auto-Pay (“Auto-Pay”) at the same time that you execute your loan agreement and remain continuously enrolled up until and through the due date of your 6th scheduled monthly loan payment obligation; (iii) remain continuously enrolled in paperless billing up until and through the due date of your 6th scheduled monthly loan payment obligation; (iv) fulfill your first 6 (six) scheduled monthly loan payment obligations in full and on time, any late, returned or missed payments will disqualify you for the Promotion; there is no grace period; and (v) not pay off your loan prior to the application of the Principal Reduction to your loan account. Once all requirements are met, the Principal Reduction will reflect on your next scheduled loan statement. The Principal Reduction will not count towards any monthly payment obligation, you must continue to make your monthly payments as scheduled. You understand that you are not required to enroll in Auto-Pay unless you want to take advantage of this promotional offer, and your willingness to enroll in Auto-Pay will not affect the lender’s credit decision regarding your loan application. Limit one (1) Principal Reduction per loan. Promotion cannot be transferred or assigned. Promotion cannot be combined, if you qualify for this Promotion and any other promotion, such as the ‘No Interest on Principal Option Promotion’, you will only receive the promotion that has the higher value. This Promotion is subject to change or discontinuation without notice.
                </p>
              </Column>
            </Grid>
          </div>
        </div>
      </section>
    </>
  );
};

export default ConnectBankAccount;
